import { z } from "zod";
import { CollectionEntity } from "@busy-human/gearbox";
import { Builders } from "../Builders/index.js";
import { Trips } from "../Trips/index.js";
import { BuilderDocuments } from "../BuilderDocuments/index.js";
import { AssignedChecklistItem } from "../AssignedChecklistItems/index.js";
function validatePreferences(schema) {
    for (const pref of schema.preferences) {
        if (!(pref in schema.preferenceDetail)) {
            return `${pref} must be included in preferenceDetail`;
        }
        ;
    }
    for (const pref in schema.preferenceDetail) {
        if (!(schema.preferences.includes(pref))) {
            return `${pref} must be included in preferences`;
        }
    }
    if (!schema.preferences.every((val, _, arr) => arr.indexOf(val) === arr.lastIndexOf(val))) {
        return `Preference list must be unique`;
    }
}
export function checkIfDeletionisValid(record) {
    if (record.registeredDate || record.status === 'assigned' || record.tripAssigned || record.depositPaid || record.policyStatus !== 'not-started' || record.depositPending || record.onWaitlist) {
        return 'contact-hxp';
    }
    else if (record.preferences.length > 0) {
        return 'cleanup';
    }
    else {
        return 'valid';
    }
}
export var BuilderRecord;
(function (BuilderRecord) {
    BuilderRecord.BuilderTypeDetailSchema = z.object({
        goingOnTrip: z.boolean().default(false),
        goingAsPair: z.boolean().default(false),
        maybePair: z.boolean().optional(),
        pairUser: z.string().nullable().default(null)
    }).default({});
    BuilderRecord.PreferenceDetailSchema = z.object({
        builderGroupType: Builders.GroupSchema,
        isWaitlist: z.boolean()
    });
    BuilderRecord.StatusSchema = z.union([
        z.literal("not-started"),
        z.literal("in-progress"),
        z.literal("complete"),
        z.literal("blocked"),
        z.literal('waiting-for-review')
    ]).default('not-started');
    BuilderRecord.DocumentStatusSchema = z.object({
        status: BuilderRecord.StatusSchema,
        uploadedTimestamp: z.string().datetime().optional(),
        completedTimestamp: z.string().datetime().nullable().default(null),
        docId: z.string().optional()
    }).default({});
    BuilderRecord.BuilderFlightInfoSchema = z.object({
        meetupWayofTravel: z.string().nullable().default(null),
        meetupAirline: z.string().default(""),
        meetupFlightCode: z.string().default(""),
        meetupArrivalTime: z.string().default(""),
        departingAirport: z.string().default(""),
        meetuphomeWayofTravel: z.string().nullable().default(null),
        meetuphomeAirline: z.string().default(""),
        meetuphomeFlightCode: z.string().default(""),
        meetuphomeDepartTime: z.string().default(""),
        unaccompaniedMinor: z.boolean().default(false)
    });
    BuilderRecord.BuilderStatusSchema = z.union([
        z.literal('unassigned'),
        z.literal('assigned'),
        z.literal('canceled'),
        z.literal('completed'),
        z.literal('closed') //Builders that were not assigned a trip when the season they registered for was completed
    ]);
    BuilderRecord.PassportInformationSchema = z.object({
        firstName: z.string(),
        middleName: z.string().optional(),
        lastName: z.string(),
        number: z.string(),
        expiration: z.string(),
        issued: z.string()
    });
    BuilderRecord.AboutYouQuestionsSchema = z.object({
        freeTime: z.string(),
        musicalTalents: z.string(),
        favCandy: z.string(),
        favSnack: z.string(),
        lifeMotto: z.string(),
        roleModel: z.string(),
        pumpUpSong: z.string(),
        biggestChallenge: z.string(),
        motivation: z.string(),
        gospelFeelings: z.string(),
    });
    BuilderRecord.BuilderAboutYouQuestionsSchema = BuilderRecord.AboutYouQuestionsSchema.extend({
        whyHXP: z.string(),
    });
    BuilderRecord.ParentAboutYouQuestionsSchema = BuilderRecord.AboutYouQuestionsSchema.extend({
        childInfo: z.string(),
        hxpGoals: z.string(),
        tripHopes: z.string(),
    });
    BuilderRecord.relationshipToPatientSchema = z.union([
        z.literal("Parent"),
        z.literal("Guardian"),
        z.literal("Other"),
    ]).nullable().default(null);
    BuilderRecord.BuilderHealthSectionSchema = z.object({
        completed: z.boolean().nullable().default(null),
        completedTimestamp: z.string().datetime().nullable().default(null),
    });
    //TODO: Use
    BuilderRecord.BuilderDoctorsInfoSchema = z.object({
        doctorsName: z.string().nullable().default(null),
        doctorsEmail: z.string().email().nullable().default(null)
    });
    BuilderRecord.BuilderHealthUploadSchema = z.object({
        uploaded: z.boolean().nullable().default(null),
        uploadedTimestamp: z.string().datetime().nullable().default(null),
    });
    BuilderRecord.MedicalHistoryRequestSchema = BuilderRecord.BuilderHealthSectionSchema.extend({
        consentForRelease: z.boolean().nullable().default(null),
        relationshipToPatient: BuilderRecord.relationshipToPatientSchema
    });
    BuilderRecord.BuilderHealthRecordSchema = z.object({
        sectionA: BuilderRecord.BuilderHealthSectionSchema,
        sectionB: BuilderRecord.BuilderHealthSectionSchema,
        sectionC: BuilderRecord.BuilderDoctorsInfoSchema,
        HIPPAA: BuilderRecord.BuilderHealthSectionSchema,
        HIPPAB: BuilderRecord.MedicalHistoryRequestSchema,
        healthFormCreated: z.boolean().default(false)
    });
    BuilderRecord.PREFERENCE_LIMIT = 20;
    BuilderRecord.ModelSchema = z.object({
        // Season
        season: z.string().default(""),
        // Booking/Reg information
        tripAssigned: z.string().nullish(),
        assignedTripGroup: Trips.TripGroupSchema.nullish(),
        onWaitlist: z.boolean().optional(),
        status: BuilderRecord.BuilderStatusSchema.default('unassigned'),
        builderType: Builders.BuilderTypeSchema.default(Builders.BuilderType.Builder),
        zohoBuilderId: z.string().nullish().describe("The id of this builder inside zoho. the builderType tells if this is a parent builder id or not"),
        zohoParentBuilderId: z.string().nullish().describe("If this is a teen builder, this is the parent's builder id in zoho. NOT THE CONTACT ID"),
        zohoYouthBuilderId: z.string().nullish().describe("If this is a parent builder, this is their paired youth builder's id in zoho. NOT THE CONTACT ID"),
        registeredDate: z.string().datetime().nullish(),
        bookedDate: z.string().datetime().nullish(),
        booked: z.boolean().nullish(),
        lastDepositAttempt: z.string().datetime().nullish(),
        depositPending: z.boolean().nullish().optional(),
        depositPaid: z.boolean().nullish(),
        departureAge: z.number().nullish(),
        // Preferences
        preferences: z.string().array().max(BuilderRecord.PREFERENCE_LIMIT).default([]),
        preferenceDetail: z.record(z.string(), BuilderRecord.PreferenceDetailSchema).default({}),
        // Type
        builderTypeDetails: BuilderRecord.BuilderTypeDetailSchema,
        // boolean
        // maybeParent: z.boolean().optional(), // This lives on builderTypeDetails
        isSoloParent: z.boolean().nullish(),
        groupMeJoined: z.boolean().default(false),
        watchedPrepVideos: z.boolean().default(false),
        readyForDoctorNoteUpload: z.boolean().default(false),
        // Basic statuses
        guardianStatus: BuilderRecord.StatusSchema,
        prefStatus: BuilderRecord.StatusSchema,
        contactInfoStatus: BuilderRecord.StatusSchema,
        policyStatus: BuilderRecord.StatusSchema,
        // Packets and Documents
        builderPacket: BuilderRecord.DocumentStatusSchema.optional().nullable(),
        notarizedAuth: BuilderRecord.DocumentStatusSchema.optional().nullable(),
        userPicture: BuilderRecord.DocumentStatusSchema.optional(),
        userVisa: BuilderRecord.DocumentStatusSchema.optional().nullable(),
        userAffidavit: BuilderRecord.DocumentStatusSchema.optional().nullable(),
        immunizationRecord: BuilderRecord.DocumentStatusSchema.optional().nullable(),
        insuranceCard: BuilderRecord.DocumentStatusSchema.optional().nullable(),
        healthForm: BuilderRecord.DocumentStatusSchema.optional().nullable(),
        healthInformation: BuilderRecord.BuilderHealthRecordSchema.optional(),
        hasSoleCustody: z.boolean().optional(),
        hasCustodyAgreement: z.boolean().optional(),
        // Checklist
        builderChecklist: z.object({
            vaccination: z.boolean().default(false),
            packing: z.boolean().default(false),
            passport: z.boolean().default(false),
            departureTransport: z.boolean().default(false),
            returnTransport: z.boolean().default(false)
        }).default({}),
        // Flight Info
        flightInfo: BuilderRecord.BuilderFlightInfoSchema.optional().nullable(),
        flightInfoLastViewed: z.string().datetime().nullish(),
        //flightInfo: z.object({
        //    meetupWayofTravel: z.string(),
        //    meetupAirline: z.string(),
        //    meetupFlightCode: z.string(),
        //    meetupArrivalTime: z.string(),
        //    meetuphomeWayofTravel: z.string(),
        //    meetuphomeAirline: z.string(),
        //    meetuphomeFlightCode: z.string(),
        //    meetuphomeDepartTime: z.string(),
        //    unaccompaniedMinor: z.boolean(),
        //}).optional(),
        // First Experience
        firstExperience: z.boolean().optional(),
        // Policies
        policies: z.object({
            dealBreakers: BuilderRecord.DocumentStatusSchema,
            refundPolicy: BuilderRecord.DocumentStatusSchema,
            contingencyPlan: BuilderRecord.DocumentStatusSchema,
            vaccines: BuilderRecord.DocumentStatusSchema,
            builderAgreement: BuilderRecord.DocumentStatusSchema,
            parentBuilderAgreement: BuilderRecord.DocumentStatusSchema,
            parentBuilderRequirements: BuilderRecord.DocumentStatusSchema,
        }).default({}),
        // Misc
        backgroundCheckTime: z.string().datetime().optional().nullable(),
        parentTrainingDate: z.string().datetime().optional().nullable(),
        parentBackgroundCheck: BuilderRecord.StatusSchema.optional().nullable(),
        parentPacket: BuilderRecord.DocumentStatusSchema.optional().nullable(),
        parentTrainingSelected: BuilderRecord.StatusSchema.optional().nullable(),
        parentAtHomePacket: BuilderRecord.DocumentStatusSchema.optional().nullable(),
        aboutBuilderInfo: BuilderRecord.BuilderAboutYouQuestionsSchema.or(BuilderRecord.ParentAboutYouQuestionsSchema).optional().nullable(),
        passportInfo: BuilderRecord.PassportInformationSchema.optional().nullable(),
        donationLink: z.string().optional(),
        donationProductId: z.string().optional(),
        tripAssignmentLog: z.string().nullish(),
        zohoId: z.string().nullish(),
        tripStartDate: z.string().optional().nullable(),
        relevantContactInfoUpdated: z.string().optional(),
        hasInsurance: z.boolean().optional(),
        hasDownloadedNoInsuranceDoc: z.boolean().optional(),
    })
        // .refine(schema => validatePreferences(schema))
        .default({});
    function isBuilderQuestions(builderQuestions) {
        return builderQuestions.whyHXP !== undefined;
    }
    BuilderRecord.isBuilderQuestions = isBuilderQuestions;
    function Default() {
        return BuilderRecord.ModelSchema.parse({});
    }
    BuilderRecord.Default = Default;
    BuilderRecord.Relationships = {
        BuilderDocuments: ["BuilderDocuments", BuilderDocuments.CollectionClass],
        AssignedChecklistItems: ["AssignedChecklistItems", AssignedChecklistItem.CollectionClass],
    };
    class CollectionClass extends CollectionEntity {
        relationships = BuilderRecord.Relationships;
        defaults() {
            return Default();
        }
    }
    BuilderRecord.CollectionClass = CollectionClass;
})(BuilderRecord || (BuilderRecord = {}));
//# sourceMappingURL=index.js.map